// ============================================================
// IP_DEPOT_MATRIZ_INTEGRAL_001R
// MATRIZ DE PROYECTOS
// DIMENSIONES OFICIALES - AGOSTO 2026
// ============================================================

const normalizar = (value) =>
  String(value ?? "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_");

function numero(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const raw = String(value)
    .replace(/\$/g, "")
    .replace(/,/g, "")
    .replace(/\s/g, "")
    .replace(/%/g, "");

  const n = Number(raw);

  return Number.isFinite(n) ? n : null;
}

function primerNumero(...values) {
  for (const value of values) {
    const n = numero(value);

    if (n !== null) {
      return n;
    }
  }

  return null;
}

// ============================================================
// DIMENSION 1 - DINAMICIDAD
// ============================================================

const DINAMICIDAD = {
  EJECUCION: [1, "En ejecución"],
  EN_EJECUCION: [1, "En ejecución"],

  VENTA_ACTIVA: [2, "Fase activa de venta"],

  VENTA_DETENIDA_TEMPORALMENTE: [
    3,
    "Venta detenida temporalmente"
  ],

  VENTA_DETENIDA: [
    3,
    "Venta detenida temporalmente"
  ],

  VENTA_NO_REALIZADA: [
    4,
    "Venta no realizada"
  ],

  NO_REALIZADA: [
    4,
    "Venta no realizada"
  ],

  CERRADO_SATISFACTORIAMENTE: [
    5,
    "Cerrado satisfactoriamente"
  ],

  CERRADO: [
    5,
    "Cerrado satisfactoriamente"
  ],

  MUERTO: [6, "Muerto"]
};

export function matrizDinamicidad(project, group) {
  const status = normalizar(
    project?.situacionProyecto ??
    project?.situacion ??
    project?.SITUACION ??
    ""
  );

  let value = DINAMICIDAD[status];

  if (!value) {
    const groups = {
      VENTA: "VENTA_ACTIVA",
      PAUSADA: "VENTA_DETENIDA",
      EJECUCION: "EJECUCION",
      CERRADO: "CERRADO",
      MUERTO: "MUERTO",
      NO_REALIZADA: "VENTA_NO_REALIZADA"
    };

    value = DINAMICIDAD[groups[normalizar(group)]];
  }

  return value
    ? {
        clave: value[0],
        nombre: value[1]
      }
    : {
        clave: null,
        nombre: "Dinamicidad por verificar"
      };
}

// ============================================================
// ETAPAS COMERCIALES
// ============================================================

export const ETAPAS_COMERCIALES = {
  OPORTUNIDAD: ["Prospección", 10],
  CALIFICACION: ["Calificación", 25],
  ANALISIS: ["Análisis", 40],
  PROPUESTA: ["Propuesta / cotización", 60],
  CONTRATACION: ["Proceso de contratación", 80],
  CIERRE_FIRMA: ["Cierre y firma", 95]
};

export function matrizEtapa(project) {
  const codigo = normalizar(
    project?.etapaComercial ??
    project?.etapa_comercial ??
    project?.ETAPA_COMERCIAL ??
    ""
  );

  if (codigo === "GANADO") {
    return {
      valida: false,
      historica: true,
      codigo
    };
  }

  const value = ETAPAS_COMERCIALES[codigo];

  return value
    ? {
        valida: true,
        codigo,
        nombre: value[0],
        probabilidad: value[1]
      }
    : {
        valida: false,
        codigo
      };
}

// ============================================================
// DIMENSION 2 - TAMANO
// ============================================================

function montoBase(project) {
  const sinIva = primerNumero(
    project?.montoSinIva,
    project?.montoSinIVA,
    project?.monto_sin_iva,
    project?.subtotalContrato
  );

  if (sinIva !== null) {
    return sinIva;
  }

  const monto = primerNumero(
    project?.montoContratado,
    project?.montoEstimado,
    project?.montoOportunidad,
    project?.monto,
    project?.importe
  );

  if (monto === null) {
    return null;
  }

  if (
    project?.incluyeIVA === true ||
    project?.montoIncluyeIva === true
  ) {
    const tasa = primerNumero(
      project?.tasaIVA,
      project?.tasaIva
    );

    if (tasa === null) {
      return null;
    }

    const rate = tasa > 1 ? tasa / 100 : tasa;

    if (rate < 0 || rate > 1) {
      return null;
    }

    return monto / (1 + rate);
  }

  return monto;
}

export function matrizTamano(project) {
  const monto = montoBase(project);

  if (monto === null || monto < 0) {
    return "Tamaño por verificar";
  }

  if (monto >= 25000000) {
    return "Grande";
  }

  if (monto >= 10000000) {
    return "Mediano";
  }

  if (monto > 500000) {
    return "Chico";
  }

  return "Inhouse";
}

export function matrizTamanoLabel(project) {
  return matrizTamano(project);
}

// ============================================================
// DIMENSION 3 - FLUJO DE COBRANZA
// ============================================================

export function matrizFlujo(project, group, porcentaje) {
  const dynamic = matrizDinamicidad(project, group);

  if (![1, 5, 6].includes(dynamic.clave)) {
    return {
      aplica: false,
      etiqueta: ""
    };
  }

  const p = numero(porcentaje);

  if (p === null || p < 0) {
    return {
      aplica: true,
      etiqueta: "Flujo por verificar"
    };
  }

  if (p > 100) {
    return {
      aplica: true,
      etiqueta: "Cobranza excedente - revisar"
    };
  }

  if (p === 0) {
    return {
      aplica: true,
      etiqueta: "Licitación ganada"
    };
  }

  if (p < 50) {
    return {
      aplica: true,
      etiqueta: "Primer pago realizado"
    };
  }

  if (p < 100) {
    return {
      aplica: true,
      etiqueta: "50 % ya pagado"
    };
  }

  return {
    aplica: true,
    etiqueta: "Pagado totalmente"
  };
}

export function matrizFlujoTexto(project, group, porcentaje) {
  const result = matrizFlujo(
    project,
    group,
    porcentaje
  );

  if (!result.aplica) {
    return "";
  }

  if (
    result.etiqueta === "Flujo por verificar" ||
    result.etiqueta === "Cobranza excedente - revisar"
  ) {
    return "Flujo: " + result.etiqueta;
  }

  return (
    "Flujo: " +
    result.etiqueta +
    " · " +
    Math.round(Number(porcentaje)) +
    "% cobrado"
  );
}

// ============================================================
// DIMENSION 4 - ROI
// ============================================================

export function matrizRoiBanda(roi) {
  const value = numero(roi);

  if (value === null) {
    return {
      clave: null,
      nombre: "ROI pendiente"
    };
  }

  if (value <= 0) {
    return {
      clave: 1,
      nombre: "Pérdida o punto de equilibrio"
    };
  }

  if (value <= 10) {
    return {
      clave: 2,
      nombre: "Rentabilidad baja"
    };
  }

  if (value <= 18) {
    return {
      clave: 3,
      nombre: "Rentabilidad objetivo"
    };
  }

  return {
    clave: 4,
    nombre: "Rentabilidad sobresaliente"
  };
}

export function matrizCalcularRoi(monto, costo) {
  const m = numero(monto);
  const c = numero(costo);

  if (
    m === null ||
    c === null ||
    c <= 0
  ) {
    return null;
  }

  return ((m - c) / c) * 100;
}

// ============================================================
// VALIDACIONES DE CONSISTENCIA
// ============================================================

export function matrizValidarProyecto(
  project,
  group,
  financial = {}
) {
  const errores = [];

  const dynamic = matrizDinamicidad(project, group);
  const commercial = matrizEtapa(project);

  if (
    [2, 3, 4].includes(dynamic.clave) &&
    !commercial.valida
  ) {
    errores.push("ETAPA_COMERCIAL_OBLIGATORIA");
  }

  if (
    [1, 5, 6].includes(dynamic.clave) &&
    commercial.valida
  ) {
    errores.push("ETAPA_COMERCIAL_NO_APLICA");
  }

  const contratado = primerNumero(
    financial?.montoContratado,
    project?.montoContratado
  );

  const cobrado = primerNumero(
    financial?.cobrado,
    financial?.totalCobrado,
    project?.montoCobrado
  );

  if (
    contratado !== null &&
    cobrado !== null &&
    cobrado > contratado + 0.01
  ) {
    errores.push("COBRADO_EXCEDE_CONTRATO");
  }

  return {
    valido: errores.length === 0,
    errores
  };
}

// ============================================================
// IP_DEPOT_MATRIZ_INTEGRAL_002
//
// Etiqueta visual oficial de ROI.
// Conserva el cálculo financiero ya existente.
// No transforma margen comercial en ROI.
// ============================================================

export function matrizRoiTexto(project) {
  if (!project || typeof project !== "object") {
    return "";
  }

  const candidates = [
    project.__financialRoi,
    project.roi,
    project.ROI,
    project.rentabilidad
  ];

  let value = null;

  for (const candidate of candidates) {
    if (
      candidate === null ||
      candidate === undefined ||
      candidate === ""
    ) {
      continue;
    }

    const parsed = Number(
      String(candidate).replace("%", "").trim()
    );

    if (Number.isFinite(parsed)) {
      value = parsed;
      break;
    }
  }

  if (value === null) {
    return "";
  }

  const banda = matrizRoiBanda(value);

  if (banda.clave === null) {
    return "";
  }

  return (
    "ROI: " +
    banda.nombre +
    " · " +
    value.toLocaleString("es-MX", {
      maximumFractionDigits: 2
    }) +
    "%"
  );
}
